import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

export interface UnsavedChangesDialogProps {
  open: boolean;
  onCurrentVersion: () => void;
  onNewVersion: () => void;
  onDiscard: () => void;
}

/**
 * UnsavedChangesDialog
 * 
 * Displays when user tries to create a new workflow while having unsaved changes.
 * Matches the Angular CanvasExpAlertComponent behavior.
 * 
 * Options:
 * 1. Save to Current Version → calls handleSave, then proceeds with save-as-new-workflow
 * 2. Save as New Version → creates new version first, then proceeds with save-as-new-workflow
 * 3. Discard → ignores unsaved changes, continues with last persisted state
 */
export const UnsavedChangesDialog: React.FC<UnsavedChangesDialogProps> = ({
  open,
  onCurrentVersion,
  onNewVersion,
  onDiscard,
}) => {
  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: '#f4f6fc',
          color: '#1a4671',
          fontWeight: 600,
          fontSize: '1rem',
          borderBottom: '1px solid #e0e6f2',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pr: 1,
        }}
      >
        Unsaved Changes
        <IconButton
          onClick={onDiscard}
          size="small"
          sx={{
            color: '#666',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.05)',
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2 }}>
        <Typography sx={{ fontSize: '0.95rem', lineHeight: 1.6, color: '#333' }}>
          There are unsaved changes in the current version. Do you want to discard the
          changes, save these changes to the current version or save the changes in a
          new version before creating a new workflow?
        </Typography>
      </DialogContent>

      <DialogActions
        sx={{
          backgroundColor: '#f9fafb',
          borderTop: '1px solid #e5e7eb',
          px: 3,
          py: 2,
          gap: 1,
          justifyContent: 'flex-end',
        }}
      >
        <Button
          onClick={onDiscard}
          variant="outlined"
          sx={{
            textTransform: 'none',
            fontSize: '0.9rem',
            fontWeight: 500,
            borderColor: '#dc3545',
            color: '#dc3545',
            '&:hover': {
              backgroundColor: '#fff5f5',
              borderColor: '#c82333',
            },
          }}
        >
          Discard
        </Button>
        <Button
          onClick={onCurrentVersion}
          variant="outlined"
          sx={{
            textTransform: 'none',
            fontSize: '0.9rem',
            fontWeight: 500,
            borderColor: '#0d6efd',
            color: '#0d6efd',
            '&:hover': {
              backgroundColor: '#f0f6ff',
              borderColor: '#0b5ed7',
            },
          }}
        >
          Current Version
        </Button>
        <Button
          onClick={onNewVersion}
          variant="outlined"
          sx={{
            textTransform: 'none',
            fontSize: '0.9rem',
            fontWeight: 500,
            borderColor: '#0d6efd',
            color: '#0d6efd',
            '&:hover': {
              backgroundColor: '#f0f6ff',
              borderColor: '#0b5ed7',
            },
          }}
        >
          New Version
        </Button>
      </DialogActions>
    </Dialog>
  );
};
